import React, { useState, useEffect, useRef } from 'react';
import { NavLink, Link, useNavigate, Outlet } from 'react-router-dom';
import {
    LayoutDashboard, Users, BarChart3, GraduationCap,
    Bell, Moon, Sun, LogOut, Settings, Shield, Ticket,
    Filter, CircleDollarSign, History, Coins, CalendarRange, Mail, Plug,
    ChevronDown, Menu, X, ChevronLeft, ChevronRight, MessageSquareText
} from 'lucide-react';
import { useTheme } from '../store/ThemeContext';
import { useAuth } from '../store/AuthContext';
import Tooltip from './ui/Tooltip';
import livePulseLogo from '../assets/live-pulse-logo.png';
import favicon from '../assets/favicon.png';
import styles from './DashboardLayout.module.css';

const getInitials = (name) =>
    name.split(' ').map((w) => w[0]).join('').toUpperCase().slice(0, 2);

// Three separate groups instead of one flat filtered list — so a superadmin's sidebar reads
// as "things everyone has" vs "things only I have," instead of superadmin-only routes being
// scattered in whatever order they happen to sit in a single array.
const MAIN_NAV_ITEMS = [
    { to: '/dashboard', icon: LayoutDashboard, label: 'Dashboard' },
    { to: '/events', icon: CalendarRange, label: 'Events' },
    { to: '/responses', icon: MessageSquareText, label: 'Manage Responses' },
    { to: '/transfer-history', icon: History, label: 'Transfer History' },
    // { to: '/team', icon: Users, label: 'Team' },
    { to: '/analytics', icon: BarChart3, label: 'Analytics' },
    { to: '/tutorials', icon: GraduationCap, label: 'Tutorials' },
];

// Admin-only personal tools (deliberately not shown to superadmin — they manage the global
// equivalents of these via the Superadmin section below instead: the global profanity list
// vs. "My blocked words", global coupons vs. "Credits & Coupon").
const ACCOUNT_NAV_ITEMS = [
    { to: '/credits', icon: Coins, label: 'Credits & Coupon' },
    { to: '/my-blocked-words', icon: Filter, label: 'My blocked words' },
];

// Platform-wide controls — only a superadmin can see or reach any of these.
const SUPERADMIN_NAV_ITEMS = [
    { to: '/admin', icon: Shield, label: 'Admin' },
    { to: '/email-providers', icon: Mail, label: 'Email providers' },
    { to: '/coupons', icon: Ticket, label: 'Coupon' },
    { to: '/profanity', icon: Filter, label: 'Profanity filter' },
    { to: '/poll-charge', icon: CircleDollarSign, label: 'Pricing' },
    { to: '/integrations', icon: Plug, label: 'Integrations' },
];

const DashboardLayout = () => {
    const { isDark, toggleTheme } = useTheme();
    const { user: authUser, logout } = useAuth();
    const navigate = useNavigate();
    const [showUserMenu, setShowUserMenu] = useState(false);
    const [sidebarOpen, setSidebarOpen] = useState(false);
    const [collapsed, setCollapsed] = useState(() => {
        const saved = localStorage.getItem('lp_sidebar_collapsed');
        return saved ? JSON.parse(saved) : false;
    });
    const [showTopbarMenu, setShowTopbarMenu] = useState(false);
    const menuRef = useRef(null);
    const topbarMenuRef = useRef(null);

    const user = authUser ? { name: authUser.name, email: authUser.email } : { name: 'Guest', email: '' };
    const initials = getInitials(user.name);

    // Persist sidebar collapse state to localStorage
    useEffect(() => {
        localStorage.setItem('lp_sidebar_collapsed', JSON.stringify(collapsed));
    }, [collapsed]);

    useEffect(() => {
        const handler = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) {
                setShowUserMenu(false);
            }
            if (topbarMenuRef.current && !topbarMenuRef.current.contains(e.target)) {
                setShowTopbarMenu(false);
            }
        };
        document.addEventListener('mousedown', handler);
        return () => document.removeEventListener('mousedown', handler);
    }, []);

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const renderNavItem = (item, isSuperadmin = false) => {
        const Icon = item.icon;
        const link = (
            <NavLink
                key={item.to}
                to={item.to}
                className={({ isActive }) =>
                    `${styles.navItem} ${isSuperadmin ? styles.navItemSuperadmin : ''} ${isActive ? styles.navItemActive : ''} ${collapsed ? styles.navItemCollapsed : ''}`
                }
            >
                <span className={styles.navIconWrap}>
                    <Icon size={17} />
                </span>
                {!collapsed && (
                    <span className={styles.navLabel}>{item.label}</span>
                )}
            </NavLink>
        );
        return collapsed ? (
            <Tooltip key={item.to} title={item.label} placement="right">{link}</Tooltip>
        ) : (
            link
        );
    };

    const sidebarClass = [
        styles.sidebar,
        sidebarOpen ? styles.sidebarOpen : '',
        collapsed ? styles.sidebarCollapsed : '',
        collapsed && showUserMenu ? styles.sidebarMenuOpen : '',
    ].filter(Boolean).join(' ');

    return (
        <div className={styles.layout}>
            {/* ── Mobile overlay ── */}
            {sidebarOpen && (
                <div
                    className={styles.mobileOverlay}
                    onClick={() => setSidebarOpen(false)}
                />
            )}

            {/* ── Sidebar ──────────────────────────────────────────────── */}
            <aside className={sidebarClass}>
                {/* Logo + Collapse Toggle */}
                <div className={styles.sidebarHeader}>
                    <Link to="/dashboard" className={styles.logo}>
                        {collapsed ? (
                            <img src={favicon} alt="LivePulse" className={styles.logoFavicon} />
                        ) : (
                            <img src={livePulseLogo} alt="LivePulse" className={styles.logoBrand} />
                        )}
                    </Link>

                    {/* Desktop collapse button */}
                    <Tooltip title={collapsed ? 'Expand sidebar' : 'Collapse sidebar'} placement="right">
                        <button
                            className={styles.collapseBtn}
                            onClick={() => setCollapsed((v) => !v)}
                            aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
                        >
                            {collapsed ? <ChevronRight size={15} /> : <ChevronLeft size={15} />}
                        </button>
                    </Tooltip>

                    {/* Mobile close button */}
                    <button
                        className={styles.sidebarCloseBtn}
                        onClick={() => setSidebarOpen(false)}
                        aria-label="Close sidebar"
                    >
                        <X size={18} />
                    </button>
                </div>

                {/* Nav Items */}
                <nav className={styles.sidebarNav}>
                    <div className={styles.navSection}>
                        {!collapsed && (
                            <span className={styles.navSectionLabel}>Main</span>
                        )}
                        {MAIN_NAV_ITEMS.map((item) => renderNavItem(item))}
                    </div>

                    {authUser?.role === 'admin' && (
                        <div className={styles.navSection}>
                            {!collapsed && (
                                <span className={styles.navSectionLabel}>Account</span>
                            )}
                            {ACCOUNT_NAV_ITEMS.map((item) => renderNavItem(item))}
                        </div>
                    )}

                    {authUser?.role === 'superadmin' && (
                        <div className={`${styles.navSection} ${styles.navSectionSuperadmin}`}>
                            {!collapsed && (
                                <span className={`${styles.navSectionLabel} ${styles.navSectionLabelSuperadmin}`}>
                                    <Shield size={11} aria-hidden />
                                    Superadmin
                                </span>
                            )}
                            {SUPERADMIN_NAV_ITEMS.map((item) => renderNavItem(item, true))}
                        </div>
                    )}
                </nav>

                {/* Sidebar Footer */}
                <div className={styles.sidebarFooter}>
                    {!collapsed && (
                        <div className={styles.footerActions}>
                            <Tooltip title={isDark ? 'Light mode' : 'Dark mode'} placement="right">
                                <button
                                    className={styles.footerBtn}
                                    onClick={toggleTheme}
                                >
                                    {isDark ? <Sun size={16} /> : <Moon size={16} />}
                                    <span>{isDark ? 'Light mode' : 'Dark mode'}</span>
                                </button>
                            </Tooltip>
                            <Tooltip title="Notifications" placement="right">
                                <button className={styles.footerBtn}>
                                    <Bell size={16} />
                                    <span>Notifications</span>
                                </button>
                            </Tooltip>
                        </div>
                    )}

                    {collapsed && (
                        <div className={styles.footerActionsCollapsed}>
                            <Tooltip title={isDark ? 'Light mode' : 'Dark mode'} placement="right">
                                <button
                                    className={styles.collapseIconBtn}
                                    onClick={toggleTheme}
                                    aria-label={isDark ? 'Switch to light mode' : 'Switch to dark mode'}
                                >
                                    {isDark ? <Sun size={16} /> : <Moon size={16} />}
                                </button>
                            </Tooltip>
                            <Tooltip title="Notifications" placement="right">
                                <button className={styles.collapseIconBtn} aria-label="Notifications">
                                    <Bell size={16} />
                                </button>
                            </Tooltip>
                        </div>
                    )}

                    {/* User profile */}
                    <div className={styles.userSection} ref={menuRef}>
                        {collapsed ? (
                            <Tooltip title={user.name} placement="right">
                                <button
                                    className={`${styles.userButton} ${styles.userButtonCollapsed}`}
                                    onClick={() => setShowUserMenu((v) => !v)}
                                    aria-expanded={showUserMenu}
                                    aria-label={`User menu — ${user.name}`}
                                >
                                    <div className={styles.userAvatar}>{initials}</div>
                                </button>
                            </Tooltip>
                        ) : (
                            <>
                                <button
                                    className={styles.userButton}
                                    onClick={() => setShowUserMenu((v) => !v)}
                                    aria-expanded={showUserMenu}
                                    aria-label="User menu"
                                >
                                    <div className={styles.userAvatar}>{initials}</div>
                                    <div className={styles.userInfo}>
                                        <span className={styles.userName}>{user.name}</span>
                                        <span className={styles.userEmail}>{user.email}</span>
                                    </div>
                                    <ChevronDown
                                        size={14}
                                        className={styles.userChevron}
                                        style={{ transform: showUserMenu ? 'rotate(180deg)' : 'none' }}
                                    />
                                </button>
                            </>
                        )}
                        {showUserMenu && (
                            <div className={`${styles.userDropdown} ${collapsed ? styles.userDropdownCollapsed : ''}`}>
                                <button className={styles.dropdownItem} onClick={toggleTheme}>
                                    {isDark ? <Sun size={14} /> : <Moon size={14} />}
                                    <span>{isDark ? 'Light mode' : 'Dark mode'}</span>
                                </button>
                                <Link
                                    to="/settings"
                                    className={styles.dropdownItem}
                                    onClick={() => setShowUserMenu(false)}
                                >
                                    <Settings size={14} />
                                    <span>Settings</span>
                                </Link>
                                <div className={styles.dropdownDivider} />
                                <button
                                    className={`${styles.dropdownItem} ${styles.dropdownDanger}`}
                                    onClick={handleLogout}
                                >
                                    <LogOut size={14} />
                                    <span>Log out</span>
                                </button>
                            </div>
                        )}
                    </div>
                </div>
            </aside>

            {/* ── Main Content ──────────────────────────────────────────── */}
            <div className={`${styles.mainWrapper} ${collapsed ? styles.mainWrapperCollapsed : ''}`}>
                {/* Mobile topbar */}
                <header className={styles.mobileTopbar}>
                    <button
                        className={styles.menuBtn}
                        onClick={() => setSidebarOpen(true)}
                        aria-label="Open menu"
                    >
                        <Menu size={20} />
                    </button>
                    <Link to="/dashboard" className={styles.mobileLogo}>
                        <img src={livePulseLogo} alt="LivePulse" className={styles.logoBrand} />
                    </Link>
                    <div className={styles.topbarUser} ref={topbarMenuRef}>
                        <button
                            className={styles.topbarAvatarBtn}
                            onClick={() => setShowTopbarMenu((v) => !v)}
                            aria-expanded={showTopbarMenu}
                            aria-label="User menu"
                        >
                            <div className={styles.userAvatar} style={{ width: 30, height: 30, fontSize: 11 }}>
                                {initials}
                            </div>
                        </button>
                        {showTopbarMenu && (
                            <div className={`${styles.userDropdown} ${styles.topbarDropdown}`}>
                                <div className={styles.topbarDropdownHeader}>
                                    <div className={styles.userAvatar}>{initials}</div>
                                    <div className={styles.userInfo}>
                                        <span className={styles.userName}>{user.name}</span>
                                        <span className={styles.userEmail}>{user.email}</span>
                                    </div>
                                </div>
                                <div className={styles.dropdownDivider} />
                                <button className={styles.dropdownItem} onClick={toggleTheme}>
                                    {isDark ? <Sun size={14} /> : <Moon size={14} />}
                                    <span>{isDark ? 'Light mode' : 'Dark mode'}</span>
                                </button>
                                <Link
                                    to="/settings"
                                    className={styles.dropdownItem}
                                    onClick={() => setShowTopbarMenu(false)}
                                >
                                    <Settings size={14} />
                                    <span>Settings</span>
                                </Link>
                                <div className={styles.dropdownDivider} />
                                <button
                                    className={`${styles.dropdownItem} ${styles.dropdownDanger}`}
                                    onClick={handleLogout}
                                >
                                    <LogOut size={14} />
                                    <span>Log out</span>
                                </button>
                            </div>
                        )}
                    </div>
                </header>

                <main className={styles.mainContent}>
                    <div className={styles.pageWrap}>
                        <Outlet />
                    </div>
                </main>
            </div>
        </div>
    );
};

export default DashboardLayout;
